import { AutoField, FieldLabel } from "@measured/puck";
import { Box, useTheme } from "@mui/material";
import { t } from "i18next";
import Rotate90DegreesCcw from "@mui/icons-material/Rotate90DegreesCcw";
import AspectRatio from "@mui/icons-material/AspectRatio";

import type { RootProps } from "src/components/Puck/config/root";
import NativePuckTitleField from "src/components/Puck/config/fields/NativePuckTitleField";

type PdfSettingsValue = RootProps["pdfSettings"];

type PdfSettingsFieldProps = {
    value?: PdfSettingsValue;
    onChange: (value: PdfSettingsValue) => void;
    readOnly?: boolean;
};

export const PdfSettingsField = ({ value, onChange, readOnly }: PdfSettingsFieldProps) => {
    const theme = useTheme();
    const settings: PdfSettingsValue = { ...(value ?? {}), format: "A3" };

    const updateField = <K extends keyof NonNullable<PdfSettingsValue>>(
        key: K,
        fieldValue: NonNullable<PdfSettingsValue>[K],
    ) => {
        onChange({ ...settings, format: "A3", [key]: fieldValue });
    };

    const updateMargin = (key: keyof NonNullable<NonNullable<PdfSettingsValue>["marginsMm"]>, marginValue: number) => {
        onChange({
            ...settings,
            format: "A3",
            marginsMm: { ...settings.marginsMm, [key]: marginValue },
        });
    };

    return (
        <Box>
            <NativePuckTitleField
                label={t("puck.pdf_settings.title", "Page Settings")}
                subtitle={t("puck.pdf_settings.subtitle", "Configure PDF orientation and margins")}
                icon="settings"
            />
            <Box sx={{ pt: theme.spacing(3) }}>
                <FieldLabel
                    label={t("puck.pdf_settings.orientation", "Orientation")}
                    icon={<Rotate90DegreesCcw fontSize="small" />}
                >
                    <AutoField
                        field={{
                            type: "radio",
                            options: [
                                { label: t("puck.pdf_settings.portrait", "Portrait"), value: "portrait" },
                                { label: t("puck.pdf_settings.landscape", "Landscape"), value: "landscape" },
                            ],
                        }}
                        onChange={(v) => updateField("landscape", v as "portrait" | "landscape")}
                        value={settings.landscape ?? "portrait"}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            <Box sx={{ pt: theme.spacing(3) }}>
                <FieldLabel
                    label={t("puck.pdf_settings.margins", "Margins (mm)")}
                    icon={<AspectRatio fontSize="small" />}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ width: 86 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        {t("puck.pdf_settings.top", "Top")}
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "number", min: 0 }}
                                    onChange={(v) => updateMargin("top", v as number)}
                                    value={settings.marginsMm?.top ?? 12}
                                    readOnly={readOnly}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ width: 86 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        {t("puck.pdf_settings.left", "Left")}
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "number", min: 0 }}
                                    onChange={(v) => updateMargin("left", v as number)}
                                    value={settings.marginsMm?.left ?? 12}
                                    readOnly={readOnly}
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: 60,
                                    height: 120,
                                    border: "2px dashed #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    color: "#999",
                                }}
                            >
                                Page
                            </Box>
                            <Box sx={{ width: 86 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        {t("puck.pdf_settings.right", "Right")}
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "number", min: 0 }}
                                    onChange={(v) => updateMargin("right", v as number)}
                                    value={settings.marginsMm?.right ?? 12}
                                    readOnly={readOnly}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ width: 86 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        {t("puck.pdf_settings.bottom", "Bottom")}
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "number", min: 0 }}
                                    onChange={(v) => updateMargin("bottom", v as number)}
                                    value={settings.marginsMm?.bottom ?? 12}
                                    readOnly={readOnly}
                                />
                            </Box>
                        </Box>
                    </Box>
                </FieldLabel>
            </Box>
        </Box>
    );
};

export default PdfSettingsField;
